import { API_ROUTE } from '../constants/workerConstants';
import AxiosInstance from './http.handlers';

export const downloadFilteredExcel = async (startDate, endDate, systemId = null) => {
  try {
    // Build the query parameters
    let params = '';
    
    // Only add date parameters if they are defined
    if (startDate && endDate) {
      params = `startDate=${startDate}&endDate=${endDate}`;
    }
    
    // Add systemId parameter if provided
    if (systemId && systemId.trim() !== '') {
      // Add & only if we already have date params
      params += (params ? '&' : '') + `systemId=${encodeURIComponent(systemId.trim())}`;
    }
    
    // Use AxiosInstance to get automatic token attachment
    const url = `/${API_ROUTE.InvTreamentUsageDoc.FILTERED_EXCEL}?${params}`;
    
    const response = await AxiosInstance.get(url, {
      responseType: 'blob', // Important for binary data
    });
    
    return {
      status: true,
      data: response.data,
      headers: response.headers
    };
  } catch (error) {
    return {
      status: false,
      message: error.message
    };
  }
};

/**
 * Fetch paginated treatment usage data with date filtering
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @param {number} pageNumber - Page number for pagination (starts at 1)
 * @param {number} pageSize - Number of records per page
 * @returns {Promise<Object>} - Paginated data response
 */
export const getFilteredData = async (startDate, endDate, pageNumber = 1, pageSize = 10) => {
  try {
    // Ensure parameters are integers
    const page = parseInt(pageNumber, 10);
    const size = parseInt(pageSize, 10);
    
    // Build query parameters
    const params = new URLSearchParams({
      startDate,
      endDate,
      pageNumber: page,
      pageSize: size
    }).toString();

    // Use AxiosInstance which already handles the API prefix
    const response = await AxiosInstance.get(`/${API_ROUTE.InvTreamentUsageDoc.FILTERED_DATA}?${params}`);
    
    return response;
  } catch (error) {
    return {
      status: false,
      message: error.message || 'Failed to fetch data'
    };
  }
};

/**
 * Fetch treatment usage data filtered by system ID
 * @param {string} systemId - System ID to filter by
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @param {number} pageNumber - Page number for pagination (starts at 1)
 * @param {number} pageSize - Number of records per page
 * @returns {Promise<Object>} - Paginated data response
 */
export const getFilteredDataBySystemId = async (systemId, startDate, endDate, pageNumber = 1, pageSize = 10) => {
  try {
    // Ensure parameters are integers
    const page = parseInt(pageNumber, 10);
    const size = parseInt(pageSize, 10);
    
    // Build query parameters
    const queryParams = {
      systemId,
      pageNumber: page,
      pageSize: size
    };
    
    // Only include date parameters if they are defined
    if (startDate && endDate) {
      queryParams.startDate = startDate;
      queryParams.endDate = endDate;
    }
    
    const params = new URLSearchParams(queryParams).toString();
    
    const url = `/${API_ROUTE.InvTreamentUsageDoc.FILTERED_BY_SYSID}?${params}`;
    
    // Use AxiosInstance which handles API prefix
    const response = await AxiosInstance.get(url);
    
    return response;
  } catch (error) {
    return {
      status: false,
      message: error.message || 'Failed to fetch data'
    };
  }
};

/**
 * Download a single file by its ID
 * @param {number} id - The ID of the file to download
 * @returns {Promise<Object>} - Response with blob data
 */
export const downloadFile = async (id) => {
  try {
    if (!id) {
      return {
        status: false,
        message: 'Invalid file ID'
      };
    }
    
    // Use AxiosInstance to get automatic token attachment
    const url = `/${API_ROUTE.InvTreamentUsageDoc.DOWNLOAD_FILE}/${id}`;
    
    const response = await AxiosInstance.get(url, {
      responseType: 'blob', // Important for binary data
    });
    
    return {
      status: true,
      data: response.data,
      headers: response.headers
    };
  } catch (error) {
    return {
      status: false,
      message: error.message || 'Failed to download file'
    };
  }
};

/**
 * Download all files for a system ID within a date range as a zip archive
 * @param {string} systemId - The system ID
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @returns {Promise<Object>} - Response with blob data
 */
export const downloadAllFiles = async (systemId, startDate, endDate) => {
  try {
    if (!systemId) {
      return {
        status: false,
        message: 'SystemId is required'
      };
    }
    
    // Format dates if they are Date objects
    const formattedStartDate = typeof startDate === 'object' && startDate instanceof Date 
      ? startDate.toISOString().split('T')[0] 
      : startDate;
    
    const formattedEndDate = typeof endDate === 'object' && endDate instanceof Date 
      ? endDate.toISOString().split('T')[0] 
      : endDate;
    
    // Build the query parameters object
    const queryParamsObj = {
      systemId: systemId
    };
    
    // Only add date parameters if both are provided
    if (startDate && endDate) {
      queryParamsObj.startDate = formattedStartDate;
      queryParamsObj.endDate = formattedEndDate;
    }
    
    // Convert to URL parameters string
    const queryParams = new URLSearchParams(queryParamsObj).toString();
    
    // Use AxiosInstance to get automatic token attachment
    const url = `/${API_ROUTE.InvTreamentUsageDoc.DOWNLOAD_FILTERED_FILES}?${queryParams}`;
    
    const response = await AxiosInstance.get(url, {
      responseType: 'blob', // Important for binary data
    });
    
    return {
      status: true,
      data: response.data,
      headers: response.headers
    };
  } catch (error) {
    return {
      status: false,
      message: error.message || 'Failed to download all files'
    };
  }
};
